/**
 * The realtime channel: live presence, blurred desktop previews, live metrics, and every
 * "something changed" nudge the server sends a UI client.
 *
 * One connection for the life of the app, as `RemoteService` is one instance behind `AppServices` —
 * a hub connection per page would cost a handshake on every navigation and leave the server fanning
 * out to connections nobody is reading.
 *
 * FOUR THINGS ARE DIFFERENT FROM THE C# CLIENT, and three of them are fixes:
 *
 *   * **A subscription returns its own unsubscribe.** `RemoteService` exposes C# events and every
 *     page had to remember a matching `-=` in `OnNavigatedFrom`; six of twenty-one pages did. Here
 *     `on()` hands back a function and `Disposables` calls it, so a page cannot forget.
 *   * **Watches are re-issued after a reconnect.** `WatchDevice` is connection state on the server,
 *     so an automatic reconnect silently ends the watch: the preview freezes on its last frame and
 *     the live graphs stop, with nothing on screen saying so. The set of watched ids is kept here
 *     and replayed on `onreconnected` — which is exactly what `AgentHub.OnConnectedAsync` does for
 *     the agent side, and what the UI side never had.
 *   * **The token is fetched through `ensureFresh`.** A WebSocket cannot carry an Authorization
 *     header, so the token rides the query string; a reconnect after the access token has aged out
 *     would otherwise be refused, and the refresh has to happen before the handshake rather than
 *     after a 401 there is no way to see.
 *   * **Failure is never fatal.** Every page here is usable over REST alone, so a hub that will not
 *     connect must not fail a page load. `ensureConnected` reports false and the page carries on.
 */
import {
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel
} from '@microsoft/signalr';
import { HubRoutes, UiHubMethods, type UiHubPayloads } from '../contracts.gen.js';
import { bearer, ensureFresh } from './auth.js';

type EventName = keyof UiHubPayloads;
type Handler<K extends EventName> = (...args: UiHubPayloads[K]) => void;

let connection: HubConnection | null = null;
let starting: Promise<boolean> | null = null;

/** One fan-out set per event name, so the hub is subscribed once however many pages listen. */
const handlers = new Map<EventName, Set<(...args: unknown[]) => void>>();

/** What this client has asked to watch. Replayed after a reconnect - see the note above. */
const watchedDevices = new Set<number>();
const watchedMonitors = new Set<number>();

export function isConnected(): boolean {
  return connection !== null && connection.state === HubConnectionState.Connected;
}

/**
 * Subscribes to a hub event. Returns the unsubscribe.
 *
 * The connection-level `on` is registered once per event name and fans out here, so subscribing
 * twice to the same event does not make the server's message arrive twice.
 */
export function on<K extends EventName>(event: K, handler: Handler<K>): () => void {
  let set = handlers.get(event);
  if (!set) {
    set = new Set();
    handlers.set(event, set);
    connection?.on(event, (...args: unknown[]) => fan(event, args));
  }
  const erased = handler as unknown as (...args: unknown[]) => void;
  set.add(erased);
  return () => {
    set?.delete(erased);
  };
}

function fan(event: EventName, args: unknown[]): void {
  const set = handlers.get(event);
  if (!set) return;
  for (const handler of [...set]) {
    try {
      handler(...args);
    } catch (error) {
      // One page's handler throwing must not stop the others from seeing the message.
      console.error(`CRM: a ${event} handler threw`, error);
    }
  }
}

/**
 * Connects, once. Resolves false when the hub is unreachable.
 *
 * Idempotent and safe to call from every page's mount: a second caller awaits the first attempt
 * rather than starting a second handshake.
 */
export function ensureConnected(): Promise<boolean> {
  if (isConnected()) return Promise.resolve(true);
  starting ??= start().finally(() => {
    starting = null;
  });
  return starting;
}

async function start(): Promise<boolean> {
  if (!connection) {
    connection = new HubConnectionBuilder()
      .withUrl(HubRoutes.Ui, {
        // Refreshed before the handshake, not after a rejection: there is no 401 to catch on a
        // WebSocket upgrade, so a stale token reads as a connection that will not come up.
        accessTokenFactory: async () => {
          await ensureFresh();
          return bearer() ?? '';
        }
      })
      .withAutomaticReconnect()
      .configureLogging(LogLevel.Warning)
      .build();

    for (const event of handlers.keys()) {
      connection.on(event, (...args: unknown[]) => fan(event, args));
    }

    connection.onreconnected(() => {
      void replayWatches();
    });
  }

  if (connection.state !== HubConnectionState.Disconnected) return isConnected();

  try {
    await connection.start();
    await replayWatches();
    return true;
  } catch {
    // No live updates. Every page that uses this is correct as of its last REST read.
    return false;
  }
}

/** Re-issues every watch this client holds. The reason this module keeps the sets at all. */
async function replayWatches(): Promise<void> {
  for (const id of watchedDevices) await invoke(UiHubMethods.WatchDevice, id);
  for (const id of watchedMonitors) await invoke(UiHubMethods.WatchMonitor, id);
}

async function invoke(method: string, ...args: unknown[]): Promise<void> {
  if (!isConnected()) return;
  try {
    await connection?.invoke(method, ...args);
  } catch {
    // Transient. The watch stays in the set, so the next reconnect re-issues it.
  }
}

/**
 * Starts the blurred preview and the live-metric stream for one device.
 *
 * Recorded before the call so a watch asked for while the hub is down is still replayed when it
 * comes up - which is the case a page meets on a cold load over a slow link.
 */
export async function watchDevice(deviceId: number): Promise<void> {
  watchedDevices.add(deviceId);
  await invoke(UiHubMethods.WatchDevice, deviceId);
}

export async function unwatchDevice(deviceId: number): Promise<void> {
  watchedDevices.delete(deviceId);
  await invoke(UiHubMethods.UnwatchDevice, deviceId);
}

export async function watchMonitor(monitorId: number): Promise<void> {
  watchedMonitors.add(monitorId);
  await invoke(UiHubMethods.WatchMonitor, monitorId);
}

export async function unwatchMonitor(monitorId: number): Promise<void> {
  watchedMonitors.delete(monitorId);
  await invoke(UiHubMethods.UnwatchMonitor, monitorId);
}

/**
 * What this client currently believes it is watching.
 *
 * A watch is SERVER-side state that costs a customer's machine real work - a preview stream, a
 * two-second metric cadence, a printer polled every five seconds - so "did the page release it?" is
 * a question worth being able to ask. Nothing in the app reads this; it is for the dev handle and a
 * verification run, which have no other way to see a set held in a module.
 */
export function watching(): { devices: number[]; monitors: number[] } {
  return { devices: [...watchedDevices], monitors: [...watchedMonitors] };
}

/** Drops the connection. For sign-out: the token it authenticated with is no longer valid. */
export async function disconnect(): Promise<void> {
  watchedDevices.clear();
  watchedMonitors.clear();
  const open = connection;
  connection = null;
  handlers.clear();
  try {
    await open?.stop();
  } catch {
    /* already gone */
  }
}
